import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { setSubmissionErrors, useInput } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Prefix } from '../../../graphql/types';
import { useUploadFile } from '../../../hooks/useUploadFile';

interface FrameCapturePreviewProps {
  capturedFrame: string | null;
  onReplaceThumbnail: () => void;
  onClearFrame: () => void;
  thumbnailSource: string;
}

const FrameCapturePreview: React.FC<FrameCapturePreviewProps> = ({
  capturedFrame,
  onReplaceThumbnail,
  onClearFrame,
  thumbnailSource,
}) => {
  const [loading, setLoading] = React.useState(false);
  const { setError } = useFormContext();
  const { field } = useInput({ source: thumbnailSource });
  const { uploadImage } = useUploadFile();

  const handleReplaceThumbnail = React.useCallback(async () => {
    if (!capturedFrame) {
      return;
    }

    setLoading(true);
    setSubmissionErrors({ [thumbnailSource]: '' }, setError);

    try {
      // Convert data URL to blob
      const response = await fetch(capturedFrame);
      const blob = await response.blob();
      const file = new File([blob], 'captured-frame.webp', {
        type: 'image/webp',
      });

      // Upload the image
      const result = await uploadImage({
        file,
        prefix: Prefix.ExerciseThumbnail,
        urlWithDimensions: false,
      });

      // Update the field with the new thumbnail
      field.onChange({
        key:
          result.key && result.key.includes('/')
            ? result.key.split('/')[1]
            : null,
        url: result.url,
      });

      onReplaceThumbnail();
    } catch (error) {
      if (error instanceof Error) {
        setSubmissionErrors({ [thumbnailSource]: error.message }, setError);
      } else {
        setSubmissionErrors(
          {
            [thumbnailSource]: 'Unexpected error during thumbnail replacement',
          },
          setError,
        );
      }
    } finally {
      setLoading(false);
    }
  }, [
    capturedFrame,
    uploadImage,
    field,
    thumbnailSource,
    setError,
    onReplaceThumbnail,
  ]);

  if (!capturedFrame) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: '4px' }}>
      <Typography variant="subtitle1" gutterBottom>
        Captured Frame Preview
      </Typography>
      <img
        src={capturedFrame}
        alt="Captured frame"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: 480,
          objectFit: 'contain',
          border: '1px solid #ccc',
          marginBottom: '8px',
        }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleReplaceThumbnail}
          disabled={loading}
          size="small"
        >
          {loading ? 'Replacing...' : 'Replace Thumbnail with This Frame'}
        </Button>
        <Button
          variant="outlined"
          onClick={onClearFrame}
          disabled={loading}
          size="small"
        >
          Clear Frame
        </Button>
      </Box>
    </Box>
  );
};

export default FrameCapturePreview;
